import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast, useTheme } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import {
  profileSnapshot, isProfileDirty, profileChanges, profileCompleteness,
  validateProfile, isCouncilCodeLocked,
  aiSettings, appearanceSettings, securitySettings, visitLayoutSettings, changedKeys,
  VISIT_LAYOUT_DEFAULTS,
} from '@parsina/framework/clinical/src/screens/Settings/Settings.shared';
import { adoptFontFamily } from '@parsina/framework/clinical/src/constants/fonts';

// ─────────────────────────────────────────────────────────────────────────────
// All settings behaviour, once, for both surfaces.
//
// Two save models, chosen per section by what the control is:
//
//   • Profile — a form. Typing is not intent, so edits stay local and a SaveBar
//     commits them (§6.4). Reset restores the last saved snapshot.
//   • Toggles and the accent picker — flipping a switch IS the intent, so they
//     persist immediately and roll back if the server refuses. The task asks
//     for these to apply "immediately"; a switch that needs a second confirming
//     click is the thing users misread as broken.
//
// A failed immediate save restores the previous value rather than leaving the
// control showing a state the server does not have.
// ─────────────────────────────────────────────────────────────────────────────

// ── AI vital-alert thresholds ────────────────────────────────────────────────
// A threshold is a typed value that can be invalid, so unlike the switches
// beside it it is NOT saved per keystroke: typing «9» on the way to «92» used
// to store 9, and clearing the field stored 0 — an SpO₂ alarm that never fires.
// It is validated on blur and saved only when valid and changed.
export const THRESHOLD_BOUNDS = {
  spo2Threshold:       { min: 80,  max: 100 },
  bpSystolicThreshold: { min: 100, max: 220 },
};

const FA_AR_DIGITS = /[۰-۹٠-٩]/g;
const toAsciiDigits = (s) => String(s).replace(FA_AR_DIGITS, (d) =>
  String((d.charCodeAt(0) & 0xf) % 10));

/** `{ value }` for a valid entry, `{ error: { key, vars } }` otherwise. Pure. */
export function parseThreshold(key, raw) {
  const text = toAsciiDigits(raw ?? '').trim();
  if (!text) return { error: { key: 'validation.requiredGeneric' } };
  const n = Number(text);
  const { min, max } = THRESHOLD_BOUNDS[key];
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < min || n > max) {
    return { error: { key: 'validation.range', vars: { min, max } } };
  }
  return { value: n };
}

/**
 * The form after a new server snapshot arrives: every field the doctor edited
 * (it differs from the snapshot it was loaded from) keeps their value; every
 * untouched field takes the server's. Pure, so it is testable on its own.
 */
export function mergeSnapshot(form, prevSaved, saved) {
  const next = { ...saved };
  Object.keys(saved).forEach((k) => {
    if (form && prevSaved && form[k] !== prevSaved[k]) next[k] = form[k];
  });
  return next;
}

export function useSettingsForm() {
  const { doctor, loading, saving, error, update, updateAvatar, refetch } = useDoctor();
  const toast = useToast();
  const { setAppearance: applyAppearanceLocally } = useTheme();
  const { t, locale, setLocale, locales } = useTranslation();

  // ── Profile ────────────────────────────────────────────────────────────────
  const saved = useMemo(() => profileSnapshot(doctor), [doctor]);
  const [form, setForm] = useState(saved);
  const [errors, setErrors] = useState({});

  // A new server snapshot (a refetch after the avatar, a toggle, another tab)
  // refreshes only the fields the doctor has NOT edited. Replacing the whole
  // form here silently threw away an unsaved bio the moment the avatar changed.
  // A field counts as edited when it differs from the snapshot it was loaded
  // from; after a successful save the two agree again, so nothing sticks.
  const prevSavedRef = useRef(null);
  useEffect(() => {
    if (!doctor) return;
    const prev = prevSavedRef.current;
    prevSavedRef.current = saved;
    if (!prev) { setForm(saved); setErrors({}); return; }
    setForm((f) => mergeSnapshot(f, prev, saved));
    // Validation errors describe the doctor's own values, which survive — so
    // they stay too.
  }, [doctor, saved]);

  // Kit inputs deliver a DOM (or synthetic {target:{value}}) event. Re-validate
  // on change only once a field has already shown an error (§6.3).
  const setField = useCallback((key) => (e) => {
    const value = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  const dirty = useMemo(() => isProfileDirty(form, saved), [form, saved]);
  const completeness = useMemo(() => profileCompleteness(form), [form]);

  const resetProfile = useCallback(() => {
    setForm(saved);
    setErrors({});
  }, [saved]);

  const saveProfile = useCallback(async () => {
    const next = validateProfile(form, saved);
    if (Object.keys(next).length) {
      setErrors(next);
      return { ok: false, errors: next };
    }
    const input = profileChanges(form, saved);
    if (!Object.keys(input).length) {
      toast.info(t('settings.profile.noChanges'));
      return { ok: true, noop: true };
    }
    try {
      await update('profile', input);
      toast.success(t('settings.profile.saved'));
      return { ok: true };
    } catch (e) {
      toast.error(t('errors.save.profile'));
      return { ok: false };
    }
  }, [form, saved, update, toast, t]);

  // ── Avatar (immediate save, like the sections below) ───────────────────────
  const [avatarBusy, setAvatarBusy] = useState(false);

  const saveAvatar = useCallback(async (dataUri) => {
    setAvatarBusy(true);
    try {
      await updateAvatar(dataUri);
      toast.success(t('settings.avatar.updated'));
      return { ok: true };
    } catch (e) {
      toast.error(t('errors.avatar.save'));
      return { ok: false };
    } finally {
      setAvatarBusy(false);
    }
  }, [updateAvatar, toast, t]);

  const removeAvatar = useCallback(async () => {
    setAvatarBusy(true);
    try {
      await updateAvatar(null);
      toast.success(t('settings.avatar.removed'));
      return { ok: true };
    } catch (e) {
      toast.error(t('errors.avatar.remove'));
      return { ok: false };
    } finally {
      setAvatarBusy(false);
    }
  }, [updateAvatar, toast, t]);

  // ── Settings sections (immediate save) ─────────────────────────────────────
  const ai = useMemo(() => aiSettings(doctor), [doctor]);
  const appearance = useMemo(() => appearanceSettings(doctor), [doctor]);
  const security = useMemo(() => securitySettings(doctor), [doctor]);
  const visitLayout = useMemo(() => visitLayoutSettings(doctor), [doctor]);

  // Optimistic overlay so a switch moves under the finger while the mutation is
  // in flight. Cleared once the refetched doctor carries the same value.
  const [pending, setPending] = useState({});
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  const commit = useCallback(async (section, patch, current) => {
    const changes = changedKeys(patch, current);
    if (!Object.keys(changes).length) return true;

    setPending((p) => ({ ...p, [section]: { ...p[section], ...changes } }));
    try {
      await update(section, changes);
      // The refetch inside `update` has landed, so the overlay is now redundant.
      setPending((p) => {
        const { [section]: _dropped, ...rest } = p;
        return rest;
      });
      return true;
    } catch (e) {
      // Roll back rather than leave the control lying about server state.
      setPending((p) => {
        const { [section]: _dropped, ...rest } = p;
        return rest;
      });
      toast.error(t('errors.save.settings'));
      return false;
    }
  }, [update, toast, t]);

  const effective = useCallback(
    (section, base) => ({ ...base, ...(pending[section] || {}) }),
    [pending],
  );

  const setAiSetting = useCallback((key, value) => {
    const current = { ...ai, ...(pendingRef.current.aiAgent || {}) };
    return commit('aiAgent', { ...current, [key]: value }, current);
  }, [ai, commit]);

  const [thresholdDrafts, setThresholdDrafts] = useState({});
  const [thresholdErrors, setThresholdErrors] = useState({});
  const thresholdField = useCallback((key) => {
    const stored = { ...ai, ...(pending.aiAgent || {}) }[key];
    const draft = thresholdDrafts[key];
    const err = thresholdErrors[key];
    return {
      value: draft ?? (stored == null ? '' : String(stored)),
      error: err ? t(err.key, err.vars) : undefined,
      onChange: (e) => {
        const v = e?.target?.value ?? e;
        setThresholdDrafts((d) => ({ ...d, [key]: v }));
        // Re-validate while typing only once the field has shown an error (DC §6.3).
        if (err) {
          const r = parseThreshold(key, v);
          setThresholdErrors((x) => ({ ...x, [key]: r.error }));
        }
      },
      onBlur: () => {
        if (draft === undefined) return;
        const r = parseThreshold(key, draft);
        if (r.error) { setThresholdErrors((x) => ({ ...x, [key]: r.error })); return; }
        setThresholdErrors((x) => ({ ...x, [key]: undefined }));
        setThresholdDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
        if (r.value !== stored) setAiSetting(key, r.value);
      },
    };
  }, [ai, pending, thresholdDrafts, thresholdErrors, setAiSetting, t]);

  const setSecuritySetting = useCallback((key, value) => {
    const current = { ...security, ...(pendingRef.current.security || {}) };
    return commit('security', { ...current, [key]: value }, current);
  }, [security, commit]);

  // Appearance paints locally first so the accent switches under the cursor,
  // then persists. On failure the local paint is reverted too.
  //
  // Two painters: the accent and the type scale belong to the design system's
  // ThemeProvider; the typeFACE is the app's own (src/constants/fonts.js). Same
  // section on the wire, same optimistic-then-persist path, different brush.
  const paintAppearance = useCallback((key, value) => {
    if (key === 'fontFamily') adoptFontFamily(value);
    else applyAppearanceLocally({ [key]: value });
  }, [applyAppearanceLocally]);

  const setAppearanceSetting = useCallback(async (key, value) => {
    const current = { ...appearance, ...(pendingRef.current.appearance || {}) };
    if (value === current[key]) return true;

    paintAppearance(key, value);
    const ok = await commit('appearance', { ...current, [key]: value }, current);
    if (!ok) paintAppearance(key, current[key]);
    return ok;
  }, [appearance, commit, paintAppearance]);

  // ── Visit page layout ──────────────────────────────────────────────────────
  //
  // The editor's operations (screens/Visit/visitLayout.js) each return a PATCH
  // — only the keys they touched — which is exactly what the partial-update
  // mutation wants. Several keys may move at once (an order and a hidden list),
  // so this takes a patch rather than a single key.
  const setVisitLayout = useCallback((patch) => {
    if (!patch || !Object.keys(patch).length) return Promise.resolve(true);
    const current = { ...visitLayout, ...(pendingRef.current.visitLayout || {}) };
    return commit('visitLayout', { ...current, ...patch }, current);
  }, [visitLayout, commit]);

  // Back to the page as it shipped. Sends every key that differs from the
  // defaults — the service merges, so a key already at its default is skipped
  // by `changedKeys` and nothing is rewritten needlessly.
  const resetVisitLayout = useCallback(() => {
    const current = { ...visitLayout, ...(pendingRef.current.visitLayout || {}) };
    return commit('visitLayout', { ...current, ...VISIT_LAYOUT_DEFAULTS }, current);
  }, [visitLayout, commit]);

  // The server's copy wins over whatever this browser resolved, but ONLY when
  // the doctor has actually recorded one. `appearanceSettings()` fills in the
  // product default for an absent value, so the raw field is read here instead:
  // adopting the default would override a doctor whose device is English and
  // who has never opened this screen, which is precisely the case the
  // device-language step of the resolution order exists to serve.
  const storedLanguage = doctor?.settings?.appearance?.language ?? null;
  useEffect(() => {
    if (storedLanguage) setLocale(storedLanguage);
  }, [storedLanguage, setLocale]);

  // ── Language ───────────────────────────────────────────────────────────────
  //
  // Stored in the SAME `appearance` section as the accent, but it cannot go
  // through `setAppearanceSetting`: that paints via the design system's
  // ThemeProvider, which knows about accents and type scales and nothing about
  // locales. Language paints through the i18n provider instead — same shape,
  // different painter.
  //
  // Order matters and is the whole point of requirement 7: `setLocale` first,
  // so every screen re-renders in the new language on the next frame, and the
  // network round trip happens behind an already-switched UI. Nothing unmounts,
  // so the open tab, the scroll position and any half-filled form survive.
  //
  // The success toast is deliberately read AFTER the switch, so it arrives in
  // the language the doctor just chose — the first confirmation that it worked.
  const setLanguage = useCallback(async (next) => {
    const current = { ...appearance, ...(pendingRef.current.appearance || {}) };
    if (!next || next === current.language) return true;

    setLocale(next);
    const ok = await commit('appearance', { ...current, language: next }, current);
    if (!ok) setLocale(current.language);
    return ok;
  }, [appearance, commit, setLocale]);

  // ── Password: NOT HERE ANY MORE ────────────────────────────────────────────
  // It moved to حساب پارسینا, which is the only panel-independent place a person
  // can change one. Keeping a second writer was the whole problem: this panel
  // was the only one that HAD a password change, so every other role had none —
  // and an account that signed up by phone and never chose a password could not
  // set a first one here at all, because the form demanded the current one.
  // The Settings screens link out; `changePassword` is gone from the BFF too.


  return {
    doctor, loading, saving, error, refetch,

    // profile form. `councilLocked` is the one field rule both surfaces need
    // to render rather than merely validate — see Settings.shared.js.
    form, errors, setField, dirty, completeness, resetProfile, saveProfile,
    councilLocked: isCouncilCodeLocked(saved),

    // avatar
    avatarBusy, saveAvatar, removeAvatar,

    // settings sections — `effective` values include any in-flight change
    ai: effective('aiAgent', ai),
    appearance: effective('appearance', appearance),
    security: effective('security', security),
    visitLayout: effective('visitLayout', visitLayout),
    setAiSetting, thresholdField, setAppearanceSetting, setSecuritySetting,
    setVisitLayout, resetVisitLayout,

    // Language. `language` is what is actually ON SCREEN, not what the server
    // last stored: the two differ for the moment between the local switch and
    // the mutation landing, and the picker must show the doctor's choice
    // immediately or it reads as having ignored the click.
    language: locale,
    languages: locales,
    setLanguage,

    // password
  };
}
